/**
 * Generic email-based API + portal routing for Vesta.
 * Product-specific rules live in `src/config/emailRouting.*.json`.
 */

export type PortalPathPrefix = string;

export type RoutingTarget = {
  apiUrl: string;
  portalPathPrefix: PortalPathPrefix;
};

export type EmailMatchRule =
  | { kind: 'emails'; emails: readonly string[] }
  | { kind: 'domains'; domains: readonly string[] };

/** JSON-serializable match rule (`allowlistEnvVar` resolved at load time). */
export type EmailMatchRuleJson =
  | {
      kind: 'emails';
      emails?: readonly string[];
      allowlistEnvVar?: string;
    }
  | { kind: 'domains'; domains: readonly string[] };

export type EmailRoutingRule = {
  match: EmailMatchRule;
  route: RoutingTarget;
};

export type EmailRoutingRuleJson = {
  match: EmailMatchRuleJson;
  route: RoutingTarget;
};

/** Ordered routing rules — first email match wins. */
export type EmailRoutingDefinition = {
  rules: readonly EmailRoutingRule[];
};

export type EmailRoutingDefinitionJson = {
  rules: readonly EmailRoutingRuleJson[];
};

export type EmailRoutingConfig = {
  primaryRoute: RoutingTarget;
  portalUrl: string;
  rules: readonly EmailRoutingRule[];
};

export function parseCommaSeparatedDomains(raw: string | undefined): string[] {
  if (typeof raw !== 'string' || !raw.trim()) {
    return [];
  }
  return raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

export function parseEmailAllowlistString(raw: string | undefined): string[] {
  if (typeof raw !== 'string' || !raw.trim()) {
    return [];
  }
  return raw
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((e) => e.includes('@'));
}

/** Webpack only inlines static `process.env.FOO` — not dynamic `process.env[name]`. */
function parseAllowlistFromEnvVar(envVar: string): string[] {
  switch (envVar) {
    case 'TQL_DRUMKIT_SELF_HOSTED_EMAIL_ALLOWLIST':
      return parseEmailAllowlistString(
        process.env.TQL_DRUMKIT_SELF_HOSTED_EMAIL_ALLOWLIST
      );
    case 'TQL_STAGING_EMAIL_ALLOWLIST':
      return parseEmailAllowlistString(process.env.TQL_STAGING_EMAIL_ALLOWLIST);
    default:
      return [];
  }
}

function hydrateEmailMatchRule(match: EmailMatchRuleJson): EmailMatchRule {
  if (match.kind === 'domains') {
    return match;
  }
  const fromEnv = match.allowlistEnvVar
    ? parseAllowlistFromEnvVar(match.allowlistEnvVar)
    : [];
  const emails = [...(match.emails ?? []), ...fromEnv];
  return { kind: 'emails', emails };
}

/** Load an `emailRouting.*.json` file (resolves `allowlistEnvVar` from `process.env`). */
export function hydrateEmailRoutingDefinition(
  raw: EmailRoutingDefinitionJson
): EmailRoutingDefinition {
  return {
    rules: raw.rules.map((rule) => ({
      match: hydrateEmailMatchRule(rule.match),
      route: rule.route,
    })),
  };
}

function normalizeEmail(email: string | null | undefined): string | null {
  if (!email) {
    return null;
  }
  const t = email.trim().toLowerCase();
  return t.includes('@') ? t : null;
}

function normalizePortalUrl(url: string): string {
  return url.trim().replace(/\/+$/, '');
}

function routingTargetApiConfigured(route: RoutingTarget): boolean {
  return route.apiUrl.trim() !== '';
}

export function emailDomainMatches(
  email: string | null | undefined,
  domains: readonly string[]
): boolean {
  if (!email || domains.length === 0) {
    return false;
  }

  const lower = email.trim().toLowerCase();
  const at = lower.lastIndexOf('@');
  if (at === -1) {
    return false;
  }
  const domain = lower.slice(at + 1);

  return domains.some((d) => domain === d || domain.endsWith(`.${d}`));
}

export function emailMatchesRule(
  email: string | null | undefined,
  match: EmailMatchRule
): boolean {
  const normalized = normalizeEmail(email);
  if (!normalized) {
    return false;
  }
  if (match.kind === 'emails') {
    return match.emails.includes(normalized);
  }
  return emailDomainMatches(normalized, match.domains);
}

function pickRoutingEmail(
  mailboxEmail: string | null | undefined,
  authenticatedEmail: string | null | undefined
): string | null {
  return normalizeEmail(authenticatedEmail) ?? normalizeEmail(mailboxEmail);
}

export function portalBaseFromTarget(
  target: RoutingTarget,
  config: Pick<EmailRoutingConfig, 'portalUrl'>
): string {
  return target.portalPathPrefix
    ? `${config.portalUrl}${target.portalPathPrefix}`
    : config.portalUrl;
}

export function assembleEmailRoutingConfig(params: {
  primaryUrl: string;
  portalUrl: string;
  definition: EmailRoutingDefinition;
}): EmailRoutingConfig {
  const portal = normalizePortalUrl(params.portalUrl);

  const primaryRoute: RoutingTarget = {
    apiUrl: params.primaryUrl,
    portalPathPrefix: '',
  };

  const rules = params.definition.rules.filter((rule) =>
    routingTargetApiConfigured(rule.route)
  );

  return {
    primaryRoute,
    portalUrl: portal,
    rules,
  };
}

export function emailRoutingHasAlternateTargets(
  config: EmailRoutingConfig
): boolean {
  return config.rules.length > 0;
}

export function resolveRoutingTarget(
  mailboxEmail: string | null | undefined,
  authenticatedEmail: string | null | undefined,
  config: EmailRoutingConfig
): RoutingTarget {
  const routingEmail = pickRoutingEmail(mailboxEmail, authenticatedEmail);
  if (routingEmail) {
    for (const rule of config.rules) {
      if (
        emailMatchesRule(routingEmail, rule.match) &&
        routingTargetApiConfigured(rule.route)
      ) {
        return rule.route;
      }
    }
  }

  return config.primaryRoute;
}

export function resolveApiBaseUrl(
  mailboxEmail: string | null | undefined,
  authenticatedEmail: string | null | undefined,
  config: EmailRoutingConfig
): string {
  return resolveRoutingTarget(mailboxEmail, authenticatedEmail, config).apiUrl;
}

export function resolvePortalBaseUrl(
  mailboxEmail: string | null | undefined,
  authenticatedEmail: string | null | undefined,
  config: EmailRoutingConfig
): string {
  const target = resolveRoutingTarget(mailboxEmail, authenticatedEmail, config);
  return portalBaseFromTarget(target, config);
}
